import { Fragment, Suspense, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Outlet, useLocation } from 'react-router'

import { useReportModal } from 'components/Modal/ReportModal/ReportModal'
import { MODAL_MODE } from 'components/Modal/config'
import usePageModal from 'features/Modal/hooks/usePageModal'
import {
  matchI18nPathList,
  useMatchList,
  useNavigate,
} from 'features/i18n/router'
import {
  useGroupActions,
  useGroupOrThreadOrPostPageData,
  useResponsive,
} from 'hooks'
import { groupToGroupContextSchema, snowplowEvent } from 'lib/analytics'
import { st } from 'lib/helpers'
import { ROUTE_PATH } from 'lib/paths'
import { useCurrentUser } from 'store/api/yay/user'
import { useGroupUserRole } from './hooks/useGroupUserRole'
import { useVisitGroupMutation } from 'store/api/yay/group'

import Button from 'components/Button'
import SeoHeader from 'components/SEOHeader'
import GroupHeader from './components/GroupHeader'
import GroupNav from './components/GroupNav'

const Group = () => {
  const location = useLocation()
  const { isDesktop } = useResponsive()
  const { groupId, groupRes, isValidGroupId } = useGroupOrThreadOrPostPageData()

  const [visitGroup] = useVisitGroupMutation()

  const { t } = useTranslation()
  const navigate = useNavigate()

  const reportModal = useReportModal()
  const groupUserRole = useGroupUserRole({ groupId })
  const { generatePageModalUrl } = usePageModal()
  const { data: currentUser } = useCurrentUser()
  const { shareGroupToTimeline, attemptJoinGroup, GroupActionConfirmBox } =
    useGroupActions()

  const showHeader = !Boolean(
    useMatchList(
      [ROUTE_PATH.GROUP_SEARCH_POST(), ROUTE_PATH.GROUP_SEARCH_THREAD_POST()],
      true
    )
  )

  const menu = useMemo(() => {
    if (typeof groupRes?.data?.id === 'undefined') return []

    const _menu = []

    if (groupUserRole == 'owner' || groupUserRole == 'moderator') {
      _menu.push({
        title: t(
          'group_info_page.group_info_settings_section.group_info_settings'
        ),
        to: generatePageModalUrl(MODAL_MODE.GROUP_SETTINGS, {
          groupId,
        }),
        onClick: () =>
          snowplowEvent.edit_group({
            group: groupToGroupContextSchema({
              group: groupRes?.data,
              currentUserId: currentUser?.id,
            }),
          }),
      })
    }

    if (groupUserRole != 'owner') {
      _menu.push({
        title: t('common.report'),
        onClick: () =>
          reportModal.open({
            userId: groupRes?.data?.owner?.id?.toString?.(),
            groupId: groupRes?.data?.id?.toString?.(),
          }),
      })
    }

    if (groupRes?.data?.is_joined) {
      _menu.push({
        title: t('group_page.menu.share_to_timeline'),
        onClick: () => {
          snowplowEvent.share({
            group: groupToGroupContextSchema({
              group: groupRes?.data,
              currentUserId: currentUser?.id,
            }),
          })
          shareGroupToTimeline(groupId)
        },
      })
    }

    _menu.push({
      title: t('common.circle_detail'),
      to: ROUTE_PATH.GROUP_DETAIL(groupRes?.data?.id),
    })

    return _menu
  }, [groupRes?.data, location, currentUser])

  // Group access permission check.
  useEffect(() => {
    // Handle when invalid group id.
    if (!isValidGroupId || groupRes?.status === 'rejected') {
      navigate(ROUTE_PATH.GROUPS, { replace: true })
      return
    }

    if (!groupRes?.isSuccess) return

    // Handle when user in private group without join.
    if (groupRes.data?.is_private && !groupRes.data?.is_joined) {
      // only this 2 routes were allowed without joined.
      const shouldRedirect = !matchI18nPathList([
        ROUTE_PATH.GROUP_DETAIL(),
        ROUTE_PATH.GROUP_INVITE(),
      ])

      if (shouldRedirect) {
        navigate(ROUTE_PATH.GROUP_DETAIL(groupRes.data.id), {
          replace: true,
        })
        return
      }
    }

    visitGroup({ groupId })
  }, [isValidGroupId, groupRes, location.pathname])

  return (
    <Fragment>
      {groupRes?.data?.topic && (
        <SeoHeader
          description={st('group.description', {
            owner_name: groupRes?.data?.owner?.nickname,
            group_description: groupRes?.data?.description,
          })}
          title={st('group.title', {
            group_name: groupRes?.data?.topic,
          })}
        />
      )}

      {showHeader && (
        <div className="Box Box--ov-h">
          <div className="Group">
            <GroupHeader group={groupRes?.data} menu={menu} />

            <GroupNav />
          </div>
        </div>
      )}

      <Suspense fallback={<div />}>
        <Outlet />
      </Suspense>

      {!groupRes?.data?.is_joined && groupRes?.isSuccess && !isDesktop && (
        <div className="Group__join-button">
          <Button
            size="large"
            buttonStyle={groupRes?.data?.is_pending ? 'gray' : 'primary'}
            onClick={() => {
              attemptJoinGroup(groupRes?.data)
            }}
            icon={groupRes?.data?.is_pending ? 'clock' : undefined}
            fluid
          >
            {groupRes?.data?.is_pending
              ? t('group_page.group_nav.join_pending_group')
              : t('common.join')}
          </Button>
        </div>
      )}

      <GroupActionConfirmBox />
    </Fragment>
  )
}

export default Group
